import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext-secure';
import { calmTokens as t } from '../shared/tokens';
import { IconLeaf } from '../shared/icons';
import FallingMoney from './FallingMoney';

const inputStyle = {
  background: 'rgba(0,0,0,0.2)',
  border: `1px solid ${t.border}`,
  borderRadius: 10,
  padding: '12px 14px',
  color: t.text,
  fontSize: 14,
  fontFamily: t.fontUi,
  outline: 'none',
  transition: 'border-color .15s',
  width: '100%',
  boxSizing: 'border-box',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login, signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        if (password !== confirmPassword) {
          setError('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError('Le mot de passe doit contenir au moins 6 caractères');
          setLoading(false);
          return;
        }
        await signUp(email, password);
      }
      navigate('/dashboard');
    } catch (err) {
      console.error("Erreur d'authentification:", err);
      if (isLogin) {
        setError('Email ou mot de passe incorrect');
      } else if (err.status === 400 && err.message?.includes('email')) {
        setError('Cet email est déjà utilisé');
      } else {
        setError("Erreur lors de l'inscription : " + (err.message || 'Erreur inconnue'));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setError('');
  };

  return (
    <div
      className="calm-viewport"
      style={{
        width: '100%',
        background: t.bg,
        color: t.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: t.fontUi,
        position: 'relative',
        // Rogne le halo sans empêcher le défilement vertical quand la carte
        // dépasse la hauteur utile (paysage téléphone).
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: 'max(20px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) max(20px, env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left))',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -280,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 900,
          height: 700,
          background:
            'radial-gradient(ellipse at center, rgba(124,143,255,0.14), rgba(124,143,255,0) 65%)',
          pointerEvents: 'none',
        }}
      />

      <FallingMoney />

      <form
        onSubmit={handleSubmit}
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 440,
          background: t.surface,
          border: `1px solid ${t.borderS}`,
          borderRadius: 20,
          padding: 36,
          boxShadow: '0 40px 80px rgba(0,0,0,0.55)',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: `linear-gradient(140deg, ${t.accent}, ${t.accentStrong})`,
              display: 'grid',
              placeItems: 'center',
              color: t.bg,
            }}
            aria-hidden="true"
          >
            <IconLeaf size={18} />
          </div>
          <span
            style={{
              fontFamily: t.fontDisp,
              fontSize: 18,
              fontWeight: 500,
              letterSpacing: -0.3,
              color: t.text,
            }}
          >
            Ma Comptabilité
          </span>
        </div>

        <div>
          <h1
            style={{
              fontFamily: t.fontDisp,
              fontSize: 28,
              fontWeight: 500,
              letterSpacing: -0.5,
              color: t.text,
              margin: 0,
            }}
          >
            {isLogin ? 'Bon retour' : 'Bienvenue'}
          </h1>
          <div
            style={{
              fontSize: 13,
              color: t.textMute,
              marginTop: 6,
              fontFamily: t.fontUi,
            }}
          >
            {isLogin
              ? 'Connectez-vous pour accéder à vos comptes.'
              : 'Créez un compte pour commencer.'}
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: 12,
            color: t.textDim,
            letterSpacing: 0.1,
          }}
        >
          Email
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.com"
            required
            autoComplete="email"
            inputMode="email"
            enterKeyHint="next"
            style={inputStyle}
          />
        </label>

        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: 12,
            color: t.textDim,
            letterSpacing: 0.1,
          }}
        >
          Mot de passe
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete={isLogin ? 'current-password' : 'new-password'}
            enterKeyHint={isLogin ? 'go' : 'next'}
            style={inputStyle}
          />
        </label>

        {!isLogin && (
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              fontSize: 12,
              color: t.textDim,
              letterSpacing: 0.1,
            }}
          >
            Confirmer le mot de passe
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              enterKeyHint="go"
              style={inputStyle}
            />
          </label>
        )}

        {error && (
          <div
            role="alert"
            style={{
              fontSize: 12,
              color: t.debit,
              background: t.debitDim,
              border: `1px solid ${t.debitBorder}`,
              padding: '10px 12px',
              borderRadius: 10,
              fontFamily: t.fontUi,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            marginTop: 4,
            padding: '12px 16px',
            background: t.text,
            border: 'none',
            color: t.bg,
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 500,
            cursor: loading ? 'progress' : 'pointer',
            fontFamily: t.fontUi,
            letterSpacing: -0.1,
            opacity: loading ? 0.7 : 1,
            transition: 'opacity .15s',
          }}
        >
          {loading
            ? isLogin
              ? 'Connexion…'
              : 'Inscription…'
            : isLogin
              ? 'Se connecter'
              : "S'inscrire"}
        </button>

        <button
          type="button"
          onClick={toggleForm}
          style={{
            background: 'transparent',
            border: 'none',
            color: t.accent,
            fontSize: 13,
            fontFamily: t.fontUi,
            cursor: 'pointer',
            padding: 6,
            letterSpacing: -0.1,
          }}
        >
          {isLogin
            ? "Pas encore de compte ? S'inscrire"
            : 'Déjà un compte ? Se connecter'}
        </button>
      </form>
    </div>
  );
}
